'use client';

import { c as _c } from "react/compiler-runtime";
import type { ActiveWallet, AddOutcomeResult, SigningDeps, TurnkeyDeps } from '@morfi/backend-sdk-v2/client';
import { useAddOutcome, usePinOutcomeMetadata } from '@morfi/backend-sdk-v2/client';
import { deriveOutcomeMintPda } from '@morfi/contract-sdk';
import { type Connection, PublicKey } from '@solana/web3.js';
export interface AddOutcomeMutationParams {
  suggestionId: string;
  marketPubkey: string;
  marketConfigBase58: string;
  eventScheduleBase58: string | null;
  outcomeIndex: number;
  label: string;
  ticker: string;
}
export interface UseAddOutcomeMutationApi {
  mutate: (params: AddOutcomeMutationParams, options?: {
    onSuccess?: (result: AddOutcomeResult) => void;
    onError?: (error: Error) => void;
  }) => void;
  isPending: boolean;
  error: Error | null;
  reset: () => void;
}
export interface UseAddOutcomeMutationDeps {
  readonly connection: Connection;
  readonly submitTransaction: Parameters<typeof useAddOutcome>[0]['submitTransaction'];
  readonly activeWallet: ActiveWallet | null;
  readonly signingDeps: SigningDeps;
  readonly turnkeyDeps: TurnkeyDeps;
}
export function useAddOutcomeMutation(deps) {
  const $ = _c(17);
  let t0;
  if ($[0] !== deps.activeWallet) {
    t0 = deps.activeWallet ?? {
      id: "__unauth__",
      address: "11111111111111111111111111111111",
      type: "embedded"
    };
    $[0] = deps.activeWallet;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  let t1;
  if ($[2] !== deps.connection || $[3] !== deps.signingDeps || $[4] !== deps.submitTransaction || $[5] !== deps.turnkeyDeps || $[6] !== t0) {
    t1 = {
      connection: deps.connection,
      submitTransaction: deps.submitTransaction,
      activeWallet: t0,
      signingDeps: deps.signingDeps,
      turnkeyDeps: deps.turnkeyDeps
    };
    $[2] = deps.connection;
    $[3] = deps.signingDeps;
    $[4] = deps.submitTransaction;
    $[5] = deps.turnkeyDeps;
    $[6] = t0;
    $[7] = t1;
  } else {
    t1 = $[7];
  }
  const add = useAddOutcome(t1);
  const pinMutation = usePinOutcomeMetadata();
  let t2;
  if ($[8] !== add || $[9] !== deps.activeWallet || $[10] !== pinMutation) {
    t2 = function mutate(params, options) {
      if (deps.activeWallet === null) {
        options?.onError?.(new Error("AUTH_REQUIRED: sign in to add an outcome"));
        return;
      }
      (async () => {
        ;
        try {
          const market = new PublicKey(params.marketPubkey);
          const [outcomeMint] = deriveOutcomeMintPda(market, params.outcomeIndex);
          const pinResult = (await pinMutation.mutateAsync({
            id: params.suggestionId,
            outcomeMintPubkey: outcomeMint.toBase58(),
            outcomeIndex: params.outcomeIndex
          })) as {
            uri: string;
            cid: string;
          };
          await add.mutate({
            marketPubkeyBase58: params.marketPubkey,
            marketConfigBase58: params.marketConfigBase58,
            eventScheduleBase58: params.eventScheduleBase58,
            outcomeIndex: params.outcomeIndex,
            name: params.label,
            symbol: params.ticker,
            ticker: params.ticker,
            uri: pinResult.uri
          }, {
            onSuccess: result => options?.onSuccess?.(result),
            onError: error_0 => options?.onError?.(error_0)
          });
        } catch (t3) {
          const error = t3;
          options?.onError?.(error instanceof Error ? error : new Error(String(error)));
        }
      })();
    };
    $[8] = add;
    $[9] = deps.activeWallet;
    $[10] = pinMutation;
    $[11] = t2;
  } else {
    t2 = $[11];
  }
  const mutate = t2;
  const t3 = add.isPending || pinMutation.isPending;
  let t4;
  if ($[12] !== add.error || $[13] !== add.reset || $[14] !== mutate || $[15] !== t3) {
    t4 = {
      mutate,
      isPending: t3,
      error: add.error,
      reset: add.reset
    };
    $[12] = add.error;
    $[13] = add.reset;
    $[14] = mutate;
    $[15] = t3;
    $[16] = t4;
  } else {
    t4 = $[16];
  }
  return t4;
}